import { forwardRef, useEffect, useState } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';

import PageLinks from './PageLinks.jsx';

const LdsPagination = forwardRef(function LdsPagination({
  currentPage = 1,
  isDisabled = false,
  navAriaLabel = 'Pagination',
  nextBtnAriaLabel = 'Next page',
  nextBtnTextCondensed = 'Next',
  nextBtnTextExpanded = 'Next',
  onLinkClick = () => {},
  onNextClick = () => {},
  onPrevClick = () => {},
  pageLinkAriaLabel = 'Page',
  pageRangeVisible = 11,
  prevBtnAriaLabel = 'Previous page',
  prevBtnTextCondensed = 'Prev',
  prevBtnTextExpanded = 'Previous',
  setCurrentPage = () => {},
  totalPages = 10,
  useButtons = false,
  className = '',
  classes,
  ...rest
}, ref) {
  const [firstPageVisible, setFirstPageVisible] = useState(1);
  const [lastPageVisible, setLastPageVisible] = useState(pageRangeVisible);

  const handleLinkClick = (e, value) => {
    e.preventDefault();
    onLinkClick(e, value);
    setCurrentPage(value);
  };

  const handlePrevClick = (e, value) => {
    e.preventDefault();
    onPrevClick(e, value);
    setCurrentPage(currentPage - 1);
  };

  const handleNextClick = (e, value) => {
    e.preventDefault();
    onNextClick(e, value);
    setCurrentPage(currentPage + 1);
  };

  useEffect(() => {
    // handle edge cases
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    } else if (currentPage < 1) {
      setCurrentPage(1);
    }
    // update visible pages
    if (currentPage >= Math.ceil(pageRangeVisible / 2) && currentPage <= (totalPages - Math.floor(pageRangeVisible / 2))) {
      setFirstPageVisible(currentPage - Math.floor(pageRangeVisible / 2));
      setLastPageVisible(currentPage + Math.floor(pageRangeVisible / 2));
    }
  }, [currentPage]);

  useEffect(() => {
    // update visible pages
    if (currentPage < Math.ceil(pageRangeVisible / 2)) {
      setFirstPageVisible(1);
      setLastPageVisible(pageRangeVisible);
    } else if (currentPage > (totalPages - Math.ceil(pageRangeVisible / 2))) {
      setFirstPageVisible(totalPages - pageRangeVisible + 1);
      setLastPageVisible(totalPages);
    } else {
      setFirstPageVisible(currentPage - Math.floor(pageRangeVisible / 2));
      setLastPageVisible(currentPage + Math.floor(pageRangeVisible / 2));
    }
  }, [pageRangeVisible]);

  return (
    <nav
      aria-label={navAriaLabel}
      className={cn(
        'lds-pagination',
        className,
        classes
      )}
      ref={ref}
      {...rest}
    >
      <ol className="lds-pagination-list">
        <PageLinks
          currentPage={currentPage}
          isDisabled={isDisabled}
          nextBtnAriaLabel={nextBtnAriaLabel}
          nextBtnTextCondensed={nextBtnTextCondensed}
          nextBtnTextExpanded={nextBtnTextExpanded}
          onLinkClick={handleLinkClick}
          onNextClick={handleNextClick}
          onPrevClick={handlePrevClick}
          lastPageVisible={lastPageVisible}
          firstPageVisible={firstPageVisible}
          pageLinkAriaLabel={pageLinkAriaLabel}
          prevBtnAriaLabel={prevBtnAriaLabel}
          prevBtnTextCondensed={prevBtnTextCondensed}
          prevBtnTextExpanded={prevBtnTextExpanded}
          totalPages={totalPages}
          useButtons={useButtons}
        />
      </ol>
    </nav>
  );
});

LdsPagination.propTypes = {
  classes: PropTypes.string,
  currentPage: PropTypes.number.isRequired,
  isDisabled: PropTypes.bool,
  navAriaLabel: PropTypes.string,
  nextBtnAriaLabel: PropTypes.string,
  nextBtnTextCondensed: PropTypes.string,
  nextBtnTextExpanded: PropTypes.string,
  onLinkClick: PropTypes.func,
  onNextClick: PropTypes.func,
  onPrevClick: PropTypes.func,
  pageLinkAriaLabel: PropTypes.string,
  pageRangeVisible: PropTypes.number,
  prevBtnAriaLabel: PropTypes.string,
  prevBtnTextCondensed: PropTypes.string,
  prevBtnTextExpanded: PropTypes.string,
  setCurrentPage: PropTypes.func.isRequired,
  totalPages: PropTypes.number,
  useButtons: PropTypes.bool,
};

export default LdsPagination;
